import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ScreenHelp } from "@/components/Help";
import type { ScreenId } from "@/components/Shell";
import { ErrorState, LoadingState } from "@/components/States";
import { Toast } from "@/components/Toast";
import { Card, Icon, Pill, Select, StatusBadge, TextInput } from "@/components/ui";
import {
  type ImportPlan,
  exportQuotesBundle,
  importQuoteBundle,
  previewImportBundle,
} from "@/lib/api";
import {
  type Customer,
  MARGIN_FLOOR,
  type Quote,
  type QuoteTotals,
  STATUS,
  type StatusKey,
  customerNameFrom,
  fmt,
  isoDate,
  quoteTotals,
  recentMonths,
} from "@/lib/data";
import { exportFileName, quoteListSheet } from "@/lib/excel";
import { deleteQuote, useCustomers, useQuotes } from "@/lib/hooks";
import { searchNorm } from "@/lib/search";
import { cols, useIsMobile } from "@/lib/useViewport";
import { downloadXlsx } from "@/lib/xlsx";
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from "react";

type Row = Quote & { t: QuoteTotals; custName: string };
type SortKey = "id" | "amount" | "margin" | "updated";

const LIST_HELP = [
  "상단 칩으로 상태(작성중/제출/수주/실패)별 필터링, 검색창에서 견적번호 · 프로젝트 · 품목으로 검색합니다.",
  "고객사 · 기간 · 담당자 드롭다운 필터를 조합할 수 있고, 컬럼 헤더(견적번호/제출금액/영업이익률/수정일) 클릭으로 정렬합니다.",
  "영업이익률이 최소 마진 미만이면 빨간 경고 아이콘으로 표시됩니다.",
  "행을 클릭하면 견적 상세로 이동하고(작성 중 견적은 바로 수정 화면), '새 견적' 버튼으로 작성 화면을 엽니다.",
  "하단에 현재 필터 기준 표시 건수와 제출 총액 합계가 표시됩니다.",
];

// 조회 조건 캐시 — 견적 상세로 이동하면 ListScreen 이 언마운트되므로, 상세의 "견적 리스트"
// 버튼으로 돌아왔을 때 고객사·기간·검색어 등 조회 화면이 그대로 유지되게 모듈 스코프에 보관한다
// (내용추가11 시트5). 새로고침/재로그인 시엔 초기화 — 의도된 동작.
// ponytail: 모듈 변수. URL 라우팅(react-router 미도입)이 생기면 쿼리스트링으로 옮긴다.
const filterCache = {
  status: "all" as "all" | StatusKey,
  custId: "",
  owner: "",
  period: "",
  q: "",
  sort: { k: "updated" as SortKey, dir: "desc" as "asc" | "desc" },
};

const FilterChip = ({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) => {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: active ? 700 : 400,
        border: 0,
        cursor: "pointer",
        padding: "8px 16px",
        borderRadius: 9999,
        whiteSpace: "nowrap",
        background: active
          ? "var(--bg-inverse)"
          : h
            ? "var(--bg-surface-3)"
            : "var(--bg-surface-2)",
        color: active ? "var(--text-inverse)" : "var(--text-near)",
        transition: "background 120ms",
      }}
    >
      {children}
    </button>
  );
};

const ListRow = ({
  r,
  selected,
  onToggle,
  onClick,
  onCopy,
  onDelete,
}: {
  r: Row;
  selected: boolean;
  onToggle: () => void;
  onClick: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) => {
  const mobile = useIsMobile();
  const [h, setH] = useState(false);
  const low = r.t.profitRate < MARGIN_FLOOR && r.status !== "lost";
  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        cursor: "pointer",
        background: selected ? "var(--bg-surface-3)" : h ? "var(--bg-surface-2)" : "transparent",
        borderTop: "1px solid var(--table-border)",
      }}
    >
      <td style={{ padding: "14px 16px", textAlign: "center" }}>
        <input
          type="checkbox"
          checked={selected}
          title="내보내기 선택"
          onClick={(e) => e.stopPropagation()}
          onChange={onToggle}
          style={{ cursor: "pointer" }}
        />
      </td>
      <td
        style={{
          padding: "14px 16px",
          fontFamily: "ui-monospace, monospace",
          fontSize: 12.5,
          color: "var(--text-near)",
          whiteSpace: "nowrap",
        }}
      >
        {r.id}
      </td>
      <td style={{ padding: "14px 16px" }}>
        <div style={{ fontWeight: 700 }}>{r.custName}</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--text-dim)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 300,
          }}
        >
          {r.project}
        </div>
      </td>
      <td
        style={{
          padding: "14px 16px",
          textAlign: "right",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmt(r.t.submit)}
      </td>
      <td
        style={{
          padding: "14px 16px",
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          color: low ? "var(--text-negative)" : "var(--text-near)",
          fontWeight: low ? 700 : 400,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            justifyContent: "flex-end",
          }}
        >
          {low && <Icon name="alert" size={13} color="var(--text-negative)" />}
          {r.t.profitRate.toFixed(1)}%
        </span>
      </td>
      <td style={{ padding: "14px 16px", textAlign: "center", color: "var(--text-near)" }}>
        {r.owner}
      </td>
      <td
        style={{
          padding: "14px 16px",
          textAlign: "center",
          color: "var(--text-dim)",
          fontSize: 12.5,
          whiteSpace: "nowrap",
        }}
      >
        {r.updated.slice(5)}
      </td>
      <td style={{ padding: "14px 16px", textAlign: "center" }}>
        <StatusBadge status={r.status} size="sm" />
      </td>
      <td style={{ padding: "14px 16px", textAlign: "center" }}>
        <button
          type="button"
          title="견적 복사 — 이 견적을 복제해 새 견적 작성"
          onClick={(e) => {
            e.stopPropagation();
            onCopy();
          }}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--text-dim)",
            cursor: "pointer",
            display: "inline-flex",
            padding: mobile ? 12 : 4,
          }}
        >
          <Icon name="copy" size={16} />
        </button>
      </td>
      <td style={{ padding: "14px 16px", textAlign: "center" }}>
        <button
          type="button"
          title="견적 삭제"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--text-dim)",
            cursor: "pointer",
            display: "inline-flex",
            padding: mobile ? 12 : 4,
          }}
        >
          <Icon name="trash" size={16} />
        </button>
      </td>
    </tr>
  );
};

export const ListScreen = ({
  onOpenQuote,
  onEditQuote,
  onCopyQuote,
  onNavigate,
  globalQuery,
}: {
  onOpenQuote: (id: string) => void;
  onEditQuote: (id: string) => void;
  onCopyQuote: (id: string) => void;
  onNavigate: (id: ScreenId) => void;
  globalQuery: string;
}) => {
  const mobile = useIsMobile();
  const quotesRes = useQuotes();
  const customersRes = useCustomers();
  const quotes = quotesRes.data;
  const customers: Customer[] = customersRes.data;
  // 담당자 필터 옵션 — 실제 견적에 등록된 담당자(중복 제거). 담당자는 고객사별 연락처에서 옴.
  const ownerNames = [...new Set(quotes.map((x) => x.owner).filter(Boolean))].sort();

  // 초기값은 filterCache — 견적 상세에서 돌아왔을 때 직전 조회 조건을 복원한다.
  const [status, setStatus] = useState<"all" | StatusKey>(filterCache.status);
  const [custId, setCustId] = useState(filterCache.custId);
  const [owner, setOwner] = useState(filterCache.owner);
  const [period, setPeriod] = useState(filterCache.period);
  const [q, setQ] = useState(filterCache.q);
  const [sort, setSort] = useState<{ k: SortKey; dir: "asc" | "desc" }>(filterCache.sort);
  useEffect(() => {
    Object.assign(filterCache, { status, custId, owner, period, q, sort });
  }, [status, custId, owner, period, q, sort]);
  const [delTarget, setDelTarget] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<{ file: File; plan: ImportPlan } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const query = searchNorm(q || globalQuery || "");

  const flashToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const toggleSel = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const onExportSelected = async () => {
    if (!selected.size) return;
    setExporting(true);
    try {
      const blob = await exportQuotesBundle([...selected]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quotes-${selected.size}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      flashToast(`${selected.size}건을 내보냈습니다`);
    } catch (err) {
      flashToast(err instanceof Error ? err.message : "내보내기 실패");
    } finally {
      setExporting(false);
    }
  };

  // 1단계: 파일 선택 → 서버에 미리보기 요청 → 확인 팝업 표시
  const onImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // 같은 파일 재선택도 change 이벤트가 나도록 초기화
    if (!file) return;
    setImporting(true);
    try {
      const plan = await previewImportBundle(file);
      setPending({ file, plan });
    } catch (err) {
      flashToast(err instanceof Error ? err.message : "가져오기 미리보기 실패");
    } finally {
      setImporting(false);
    }
  };

  // 2단계: 확인 → 실제 import
  const confirmImport = async () => {
    if (!pending) return;
    setImporting(true);
    try {
      const imported = await importQuoteBundle(pending.file);
      await quotesRes.reload();
      setPending(null);
      flashToast(
        imported.length === 1
          ? `견적 가져오기 완료 (${imported[0].id})`
          : `견적 ${imported.length}건 가져오기 완료`
      );
    } catch (err) {
      flashToast(err instanceof Error ? err.message : "가져오기 실패");
    } finally {
      setImporting(false);
    }
  };

  const importMessage = (plan: ImportPlan): ReactNode => {
    const lines: string[] = [];
    if (plan.newCustomers.length)
      lines.push(`고객사 ${plan.newCustomers.length}건: ${plan.newCustomers.join(", ")}`);
    if (plan.newMaterials.length)
      lines.push(`자재 ${plan.newMaterials.length}건: ${plan.newMaterials.join(", ")}`);
    if (plan.newSurfaces.length)
      lines.push(`표면처리 ${plan.newSurfaces.length}건: ${plan.newSurfaces.join(", ")}`);
    if (plan.newLaborRates.length)
      lines.push(`노무단가 ${plan.newLaborRates.length}건: ${plan.newLaborRates.join(", ")}`);
    return (
      <>
        {plan.quoteCount === 1 ? (
          <span>
            <b>{plan.quotes[0]?.sourceQuoteId || "견적"}</b>
            {plan.quotes[0]?.project ? ` (${plan.quotes[0].project})` : ""} 을(를) 이 계정으로
            가져옵니다.
          </span>
        ) : (
          <span>
            견적 <b>{plan.quoteCount}건</b>을 이 계정으로 가져옵니다.
          </span>
        )}
        {plan.storageRequired && (
          <div style={{ color: "#d33", marginTop: 8 }}>
            ⚠ 이 환경은 이미지 저장소가 비활성이라 이미지 포함 가져오기가 실패할 수 있습니다.
          </div>
        )}
        {lines.length ? (
          <div style={{ marginTop: 10 }}>
            이 계정에 <b>새로 추가</b>될 데이터:
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            새로 추가될 참조 데이터는 없습니다(기존 데이터 재사용). 견적만 생성됩니다.
          </div>
        )}
        {plan.fileCount > 0 && (
          <div style={{ marginTop: 8, color: "var(--text-dim)" }}>
            이미지·첨부 {plan.fileCount}개 포함.
          </div>
        )}
      </>
    );
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteQuote(delTarget.id);
      await quotesRes.reload();
      setDelTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  let rows: Row[] = quotes.map((x) => ({
    ...x,
    t: quoteTotals(x.items),
    custName: customerNameFrom(customers, x.customerId),
  }));
  rows = rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (custId && r.customerId !== custId) return false;
    if (owner && r.owner !== owner) return false;
    if (period && !r.created.startsWith(period)) return false;
    if (query) {
      // CNC 견적의 품명·품번은 items 가 아니라 cnc[].selection 에 있다 — 이걸 빼먹어
      // 견적서에 등록된 품명("LOCATOR INSERT")으로 검색해도 0건이었다(내용추가11 시트4).
      // 품명은 개행 입력이 가능하므로 정규화(개행→공백)해야 "LOCATOR INSERT" 로도 잡힌다.
      const hay = searchNorm(
        r.id +
          r.custName +
          r.project +
          r.items.map((i) => i.item).join(" ") +
          r.cnc.map((p) => `${p.selection.itemName} ${p.selection.partNo ?? ""}`).join(" ")
      );
      if (!hay.includes(query)) return false;
    }
    return true;
  });
  rows.sort((a, b) => {
    const dir = sort.dir === "asc" ? 1 : -1;
    let av: string | number;
    let bv: string | number;
    if (sort.k === "amount") {
      av = a.t.submit;
      bv = b.t.submit;
    } else if (sort.k === "margin") {
      av = a.t.profitRate;
      bv = b.t.profitRate;
    } else {
      av = a[sort.k];
      bv = b[sort.k];
    }
    return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
  });

  const setSortK = (k: SortKey) =>
    setSort((s) => ({ k, dir: s.k === k && s.dir === "desc" ? "asc" : "desc" }));

  const Th = ({
    k,
    children,
    align = "left",
  }: { k?: SortKey; children: ReactNode; align?: "left" | "right" | "center" }) => (
    <th
      onClick={k ? () => setSortK(k) : undefined}
      style={{
        textAlign: align,
        padding: "11px 16px",
        fontWeight: 700,
        fontSize: 11.5,
        color: "var(--text-dim)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        cursor: k ? "pointer" : "default",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          flexDirection: align === "right" ? "row-reverse" : "row",
        }}
      >
        {children}
        {k && sort.k === k && (
          <Icon
            name={sort.dir === "asc" ? "arrowUp" : "arrowDown"}
            size={12}
            color="var(--brand)"
          />
        )}
      </span>
    </th>
  );

  const periods = recentMonths(new Date(), 3);

  if (quotesRes.loading) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <LoadingState />
      </div>
    );
  }
  if (quotesRes.error) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <ErrorState error={quotesRes.error} onRetry={() => void quotesRes.reload()} />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: 18 }}>
      <ScreenHelp items={LIST_HELP} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <FilterChip active={status === "all"} onClick={() => setStatus("all")}>
          전체 {quotes.length}
        </FilterChip>
        {(Object.keys(STATUS) as StatusKey[]).map((k) => (
          <FilterChip key={k} active={status === k} onClick={() => setStatus(k)}>
            {STATUS[k].label} {quotes.filter((x) => x.status === k).length}
          </FilterChip>
        ))}
        <div style={{ flex: 1 }} />
        {selected.size > 0 && (
          <Pill
            variant="dark"
            size="sm"
            icon={<Icon name="save" size={16} />}
            disabled={exporting}
            onClick={() => void onExportSelected()}
          >
            {exporting ? "내보내는 중…" : `선택 내보내기 (${selected.size})`}
          </Pill>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          style={{ display: "none" }}
          onChange={(e) => void onImportFile(e)}
        />
        <Pill
          variant="outlined"
          size="sm"
          icon={<Icon name="paperclip" size={16} />}
          disabled={importing}
          onClick={() => fileRef.current?.click()}
        >
          {importing ? "가져오는 중…" : "가져오기"}
        </Pill>
        <Pill
          variant="outlined"
          size="sm"
          icon={<Icon name="download" size={16} />}
          onClick={() =>
            downloadXlsx(exportFileName("견적목록", isoDate(new Date())), [quoteListSheet(rows)])
          }
        >
          엑셀 내보내기
        </Pill>
        <Pill
          variant="primary"
          size="sm"
          icon={<Icon name="plus" size={16} />}
          onClick={() => onNavigate("new")}
        >
          새 견적
        </Pill>
      </div>

      <Card
        pad={16}
        style={{ display: "grid", gridTemplateColumns: cols(mobile, "1.4fr 1fr 1fr 1fr"), gap: 12 }}
      >
        <TextInput value={q} onChange={setQ} placeholder="견적번호 · 프로젝트 · 품목 검색" />
        <Select
          value={custId}
          onChange={setCustId}
          placeholder="고객사 전체"
          options={customers.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Select value={period} onChange={setPeriod} placeholder="전체 기간" options={periods} />
        <Select value={owner} onChange={setOwner} placeholder="담당자 전체" options={ownerNames} />
      </Card>

      <Card pad={0} style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 920 }}
          >
            <thead
              style={{
                background: "var(--table-header-bg)",
                borderBottom: "1px solid var(--table-border-strong)",
              }}
            >
              <tr>
                <th style={{ padding: "12px 16px", textAlign: "center", width: 36 }}>
                  <input
                    type="checkbox"
                    title="전체 선택"
                    checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                    onChange={(e) =>
                      setSelected((s) => {
                        const n = new Set(s);
                        for (const r of rows) {
                          if (e.target.checked) n.add(r.id);
                          else n.delete(r.id);
                        }
                        return n;
                      })
                    }
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <Th k="id">견적번호</Th>
                <Th>고객사 · 프로젝트</Th>
                <Th k="amount" align="right">
                  제출금액
                </Th>
                <Th k="margin" align="right">
                  영업이익률
                </Th>
                <Th align="center">담당</Th>
                <Th k="updated" align="center">
                  수정일
                </Th>
                <Th align="center">상태</Th>
                <Th align="center">복사</Th>
                <Th align="center">삭제</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ListRow
                  key={r.id}
                  r={r}
                  selected={selected.has(r.id)}
                  onToggle={() => toggleSel(r.id)}
                  // 작성 중(임시저장) 견적은 바로 수정 화면으로 — 그 외는 상세 조회.
                  onClick={() => (r.status === "draft" ? onEditQuote(r.id) : onOpenQuote(r.id))}
                  onCopy={() => onCopyQuote(r.id)}
                  onDelete={() => setDelTarget(r)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!rows.length && (
          <div
            style={{
              padding: "48px 0",
              textAlign: "center",
              color: "var(--text-faint)",
              fontSize: 14,
            }}
          >
            조건에 맞는 견적이 없습니다
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            padding: "14px 20px",
            borderTop: "2px solid var(--table-border-strong)",
            color: "var(--text-dim)",
            fontSize: 12.5,
          }}
        >
          <span>
            {rows.length}건 표시 · 총 {quotes.length}건
          </span>
          <span>
            제출 총액{" "}
            <b style={{ color: "var(--text-base)" }}>
              {fmt(rows.reduce((a, r) => a + r.t.submit, 0))} 원
            </b>
          </span>
        </div>
      </Card>

      {delTarget && (
        <ConfirmDialog
          title="견적 삭제"
          message={
            <>
              <b>{delTarget.id}</b> ({delTarget.custName} · {delTarget.project}) 견적을
              삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </>
          }
          confirmLabel="삭제"
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => !deleting && setDelTarget(null)}
        />
      )}
      {pending && (
        <ConfirmDialog
          title="견적 가져오기"
          message={importMessage(pending.plan)}
          confirmLabel="가져오기"
          busy={importing}
          onConfirm={() => void confirmImport()}
          onCancel={() => !importing && setPending(null)}
        />
      )}
      <Toast message={toast} />
    </div>
  );
};
